import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { LiveFeedPlayWrapper, PlaySegDiv } from './LiveFeedPlay.style';
import PlayScores from './play/PlayScores';
import PlayPitches from './play/PlayPitches';
import { PlayEventTypes } from './play/PlayEvents';
import { determineResultDisplay } from './SummaryPlay';
import { determineLiveFeedEventDisplay } from './play/utils/displayHelpers';

import SummaryPlayEvents from './play/SummaryPlayEvents';
import PlayStatus from './play/utils/PlayStatus';
import LiveFeedAnimation from './play/LiveFeedAnimation';
import FirstPitchDataContainer from '../FirstPitch/FirstPitchDataContainer';
import preGameList from '@gameday/utils/preGameList';
import { useTranslate } from '@gameday/utils/hooks/useTranslate';

const TOP = 'top';
const PLAYERCONTEXT_LIVE_TAB = 'Live Tab';

const {
    PITCH,
    NO_PITCH,
    PICKOFF,
    STEPOFF
} = PlayEventTypes;

// Build the total number of segments of the play to display
const buildPlaySegments = (
    playData,
    teams,
    offset,
    homeNameFallback,
    awayNameFallback,
    resultVideo,
    isLoading,
    language,
    deviceInteraction,
    currentPlayIndex,
    showAllPitches,
    lastRenderedPlayEvents = [],
    lastRenderedResult,
    openAtBatOverlay,
    showPitchesOnly,
    playMetrics = {},
    playerMap = {},
    showBasesText,
    challengeOutcomeOptions,
    absConfig
) => {
    const playEventMetrics = playMetrics?.playEvents || [];
    const {
        playEvents = [],
        about = {},
        result = {},
        matchup = {},
        count = {},
        atBatIndex,
        runners = []
    } = playData;

    const isCurrentPlay = currentPlayIndex === atBatIndex;
    const finalEventReviewDetails = playData?.reviewDetails;

    // These values determine how many segments to animate,
    // and how many of those should be delayed to match the
    // ChipFeed animation
    let delayedAnim = 0;
    let anim = 0;

    const hasResult = !!result.event;

    // Determine which kind of segment animations we're looking
    // for throughout the loop.
    let animatingPlayEvent = false;
    let animatingResult = false;
    if (isCurrentPlay) {
        if (hasResult && !lastRenderedResult) {
            animatingResult = true;
        }
        if (playEvents.length > lastRenderedPlayEvents) {
            animatingPlayEvent = true;
        }
    }

    const {
        isScoringPlay,
        isComplete
    } = about;

    const {
        batter = {}
    } = matchup;

    const playSegments = [];

    const universalPayload = {
        offset
    };

    const segDataTypes = [];

    const pitches = playData.pitchIndex || [];

    const showPitches = showAllPitches || isCurrentPlay;

    // Determine events where runners moved. This will signal we should show
    // bases.
    const eventsWithRunners = {};
    for (const runner of runners) {
        const {
            details: {
                playIndex
            } = {},
            movement: {
                end
            } = {}
        } = runner;
        const runnerPlayIndex = playIndex;
        if (typeof runnerPlayIndex !== 'undefined') {
            eventsWithRunners[runnerPlayIndex] = runnerPlayIndex === playEvents.length - 1 ? !!end : true;
        }
    }

    // Calculate play segments from furthest past play to most current
    // Will reverse sort in Play return func if doing reverse chron.
    let index = 0;
    for (const event of playEvents) {
        // Vars needed for animation purposes
        const latestPlayEvent = index === playEvents.length - 1;
        const isNewPlayEvent = index > lastRenderedPlayEvents - 1;
        const tickAnim = animatingPlayEvent && isNewPlayEvent;
        const tickDelay = latestPlayEvent && !isComplete;

        const eventMetrics = playEventMetrics?.[index] || {};
        const eventMetricsDefined = index < playEventMetrics?.length;
        const {
            offense: eventOffense = {}
        } = eventMetrics;

        const {
            details = {},
            type,
            count: eCount,
            pitchNumber,
            pitchData,
            reviewDetails
        } = event;

        let challengeString = '';
        const currentReviewDetails = latestPlayEvent && finalEventReviewDetails ? finalEventReviewDetails : reviewDetails; // if there is a final review from the top level playData, use that, otherwise use the reviewDetails from the event
        if (currentReviewDetails && absConfig?.pitch !== false) {
            if (currentReviewDetails?.inProgress) {
                challengeString = challengeOutcomeOptions?.inProgress;
            } else if (currentReviewDetails?.isOverturned) {
                challengeString = challengeOutcomeOptions?.isOverturned;
            } else {
                challengeString = challengeOutcomeOptions?.isUpheld;
            }
        }

        const noPitch = type === NO_PITCH; // NO_PITCH === VIOLATIONS
        const hasViolations = !!event?.details?.violation;
        const eventIndex = event?.index;

        // PLAY PITCHES & VIOLATIONS
        if ((type === PITCH || (type === NO_PITCH && hasViolations)) && (showPitches || showPitchesOnly)) {
            const {
                ballColor,
                description,
                type,
                isBall,
                isStrike,
                isInPlay,
                isInPlayOuts,
                eventType
            } = details;
            const {
                startSpeed = ''
            } = pitchData || {}; // adding a fallback to 0 because violations (no_pitch) have no speed.
            const {
                balls,
                strikes
            } = eCount;

            // Below we are splitting the violation (no_pitch) description at the hyphen and assigning the before and after, with a fallback to full description.
            let violationDesc = '';
            let violationType = '';
            if (noPitch && description) {
                const descSplit = description?.split(' - ');
                violationDesc = descSplit[0] || description;
                violationType = descSplit[1] || description;
            }

            playSegments.push(<PlayPitches
                offset={false}
                isLoading={isLoading}
                pitchNumber={noPitch ? 'A' : pitchNumber}
                ballColor={ballColor}
                description={violationDesc || (challengeString ? description + ` - ${challengeString}` : description)}
                pitchType={ violationType || type?.description }
                speed={ startSpeed }
                balls={ balls }
                strikes={ strikes }
                isBall={ isBall }
                isStrike={ isStrike }
                isInPlay={ isInPlay }
                isInPlayOuts={ isInPlayOuts }
                displayCount={ !isInPlay }
                key={`live-feed-pitch-${noPitch ? 'violation-' + eventIndex : pitchNumber}`}
                noPitch={noPitch}
            />);
            segDataTypes.push(eventType);
            if (tickAnim) {
                anim++;
                if (tickDelay) {
                    delayedAnim++;
                }
            }
        }

        // SUMMARY PLAY EVENTS
        if (!showPitchesOnly) {
            const eventQualifiesToDisplay = determineLiveFeedEventDisplay(event, isCurrentPlay);
            if (eventQualifiesToDisplay) {
                const {
                    event: e,
                    eventType,
                    description = '',
                    homeScore,
                    awayScore,
                    isOut
                } = details;

                // Determine if the playEvent resulted in an out.
                // Similar to above's logic, this is faulty
                const thisPitch = pitches && playEvents[index];
                const thisPitchDetails = thisPitch && thisPitch.details;
                const iconName = (type === PICKOFF || type === STEPOFF) ? type : eventType;
                let isPreGameEvent = false;
                let isPlayEventFirstPitch = false;

                if (atBatIndex === 0) { // to identify the first atbat
                    if (thisPitchDetails && thisPitchDetails.eventType === 'game_advisory') {
                        const pitchDescription = thisPitchDetails?.description || '';
                        isPreGameEvent = preGameList.includes(pitchDescription);
                        if (isPreGameEvent && index === 0) { // only need the first one from first atbat playEvents
                            isPlayEventFirstPitch = true;
                        }
                    }
                }

                const TempCom = !isPreGameEvent
                    ? <SummaryPlayEvents
                        event={ e }
                        language={ language }
                        teams={ teams }
                        description={ description }
                        outs={ isOut ? eCount?.outs : undefined }
                        homeScore={ homeScore }
                        awayScore={ awayScore }
                        isTopInning={ about.halfInning === TOP }
                        eventType={ eventType }
                        isLoading={ isLoading }
                        deviceInteraction={ deviceInteraction }
                        key={`live-feed-play-event-${index}`}
                        openAtBatOverlay={openAtBatOverlay}
                        atBatIndex={atBatIndex}
                        eventIcon={iconName}
                        playerContext={PLAYERCONTEXT_LIVE_TAB}
                    />
                    : isPlayEventFirstPitch ? <FirstPitchDataContainer iconName={'baseball'} showImage={true}/> : null;

                if (TempCom !== null) {
                    playSegments.push(TempCom);
                    segDataTypes.push(eventType);
                    if (tickAnim) {
                        anim++;
                        if (tickDelay) {
                            delayedAnim++;
                        }
                    }
                }
            }

            // PLAY SCORES - EVENTS
            if (details.isScoringPlay) {
                const {
                    homeScore,
                    awayScore
                } = details;

                playSegments.push(
                    <PlayScores
                        teams={ teams }
                        isTopInning={ about.halfInning === TOP }
                        homeScore={ homeScore }
                        awayScore={ awayScore }
                        key={ `live-feed-play-scores-${index}` }
                        homeNameFallback={ homeNameFallback }
                        awayNameFallback={ awayNameFallback }
                        isLoading={isLoading}
                        { ...universalPayload }
                    />
                );
                segDataTypes.push('scores');
                if (tickAnim) {
                    anim++;
                    if (tickDelay) {
                        delayedAnim++;
                    }
                }
            }

            // PLAY STATUS - EVENTS
            if (eventMetricsDefined && eventQualifiesToDisplay && eventsWithRunners[index] && index < playEvents.length - 1) {
                playSegments.push(<PlayStatus
                    basesResults={{
                        first: playerMap[eventOffense?.postOnFirst?.id],
                        second: playerMap[eventOffense?.postOnSecond?.id],
                        third: playerMap[eventOffense?.postOnThird?.id]
                    }}
                    hideCount
                    hideOuts
                    showBasesText={showBasesText}
                    basePlayers
                    key={`live-feed-play-${index}-status-runners`}
                />);
                segDataTypes.push('status');
                if (tickAnim) {
                    anim++;
                    if (tickDelay) {
                        delayedAnim++;
                    }
                }
            }

            // PLAY RESULT
            if (result?.event && index === playEvents.length - 1 && determineResultDisplay(result, about, resultVideo, { all: true })) {
                const {
                    event,
                    homeScore,
                    awayScore,
                    description,
                    isOut
                } = result;
                playSegments.push(
                    <SummaryPlayEvents
                        isResult
                        language={ language }
                        playerId={ batter?.id }
                        event={ event }
                        teams={ teams }
                        description={ description }
                        outs={ isOut ? count?.outs : undefined }
                        homeScore={ homeScore }
                        awayScore={ awayScore }
                        resultVideo={ resultVideo }
                        isTopInning={ about.halfInning === TOP }
                        isLoading={ isLoading }
                        deviceInteraction={ deviceInteraction }
                        key={`live-feed-play-${index}-result`}
                        openAtBatOverlay={openAtBatOverlay}
                        atBatIndex={atBatIndex}
                        playerContext={PLAYERCONTEXT_LIVE_TAB}
                    />
                );
                segDataTypes.push(result.event);
                if (isCurrentPlay && animatingResult) {
                    anim++;
                    delayedAnim++;
                }
            }
        }
        index++;
    }

    // PLAY SCORES - RESULT
    if (result?.event && isScoringPlay && !showPitchesOnly) {
        const {
            homeScore,
            awayScore
        } = result;

        playSegments.push(
            <PlayScores
                teams={ teams }
                isTopInning={ about.halfInning === TOP }
                homeScore={ homeScore }
                awayScore={ awayScore }
                key={ `live-feed-play-result-scores-${index}` }
                homeNameFallback={ homeNameFallback }
                awayNameFallback={ awayNameFallback }
                isLoading={isLoading}
                { ...universalPayload }
            />
        );
        segDataTypes.push('scores');
        if (isCurrentPlay && animatingResult) {
            anim++;
            delayedAnim++;
        }
    }

    // PLAY STATUS - RESULT
    if (playMetrics && eventsWithRunners[playEvents.length - 1] && !showPitchesOnly) {
        playSegments.push(<PlayStatus
            basesResults={{
                first: playerMap[matchup.postOnFirst?.id],
                second: playerMap[matchup.postOnSecond?.id],
                third: playerMap[matchup.postOnThird?.id]
            }}
            hideCount
            hideOuts
            showBasesText={showBasesText}
            basePlayers
            key={`live-feed-play-${index}-status`}
        />);
        segDataTypes.push('status');
        if (isCurrentPlay && animatingResult) {
            anim++;
            delayedAnim++;
        }
    }

    return {
        playSegments,
        delayedAnim,
        anim,
        segDataTypes
    };
};

const LiveFeedPlay = ({
    playData,
    teams,
    offset = false,
    homeNameFallback = 'HOME',
    awayNameFallback = 'AWAY',
    reverseChron = false,
    resultVideo,
    isLoading,
    language,
    deviceInteraction = {},
    currentPlayIndex,
    showAllPitches,
    showAnimations = false,
    openAtBatOverlay,
    showPitchesOnly = false,
    playMetrics,
    playerMap,
    showBasesText = true,
    playTimer = 8.5,
    absConfig
}) => {
    const [renderedPlayEvents, setRenderedPlayEvents] = useState(playData?.playEvents?.length || 0);
    const [renderedResult, setRenderedResult] = useState(playData?.result?.event || false);
    const isCurrentPlay = currentPlayIndex === playData.atBatIndex;
    const translate = useTranslate();
    const challengeOutcomeOptions = {
        inProgress: translate('abs_challenge_in_progress', 'In Progress'),
        isOverturned: translate('abs_challenge_overturned', 'Overturned'),
        isUpheld: translate('abs_challenge_upheld', 'Upheld')
    };

    // Set a "playCode". This helps cut down on rerenders in the useMemo and
    // avoid glitchy animations.
    const playCode = `${playData?.playEvents?.length}-${playData?.result?.event || 'no'}`;

    const newAnimDivs = useMemo(() => {
        const {
            playSegments,
            delayedAnim,
            anim,
            segDataTypes
        } = buildPlaySegments(
            playData,
            teams,
            offset,
            homeNameFallback,
            awayNameFallback,
            resultVideo,
            isLoading,
            language,
            deviceInteraction,
            currentPlayIndex,
            showAllPitches,
            renderedPlayEvents,
            renderedResult,
            openAtBatOverlay,
            showPitchesOnly,
            playMetrics,
            playerMap,
            showBasesText,
            challengeOutcomeOptions,
            absConfig
        );

        // reorder the playsegments based on how we want the timeline
        const orderedPlaySegments = reverseChron ? [...playSegments].reverse() : playSegments;
        const orderedDataSegments = reverseChron ? [...segDataTypes].reverse() : segDataTypes;

        // Go through the segments and set the animate flag based on values passed from
        // the playSegment builder
        const nonAnimArr = [];
        const quickAnimArr = [];
        const delayAnimArr = [];

        orderedPlaySegments.forEach((item, i) => {
            const index = playSegments.length - i;
            const animate = i < anim;
            const delay = i < delayedAnim;

            const playSegKey = `play-seg-${index}`;
            const playEl = <PlaySegDiv
                key={ playSegKey }
                id={ playSegKey }
                dataType={ orderedDataSegments[i] }
            >{item}</PlaySegDiv>;

            if (delay) {
                delayAnimArr.push(playEl);
            } else if (animate) {
                quickAnimArr.push(playEl);
            } else {
                nonAnimArr.push(playEl);
            }
        });

        const newAnimDivs = [...nonAnimArr];
        if (quickAnimArr.length) {
            newAnimDivs.unshift(<LiveFeedAnimation
                key={ `quick-anim-${orderedPlaySegments.length}` }
            >
                {quickAnimArr}
            </LiveFeedAnimation>);
        }
        if (delayAnimArr.length) {
            newAnimDivs.unshift(<LiveFeedAnimation
                key={ `delay-anim-${orderedPlaySegments.length}` }
                delay={ playTimer }
            >
                {delayAnimArr}
            </LiveFeedAnimation>);
        }

        // Set the currently viewed playevents and result so we know what to animate
        // for next time
        if (isCurrentPlay) {
            setRenderedPlayEvents(playData?.playEvents?.length);
            setRenderedResult(!!playData?.result?.event);
        }

        return newAnimDivs;
    }, [
        currentPlayIndex,
        playCode,
        isCurrentPlay,
        resultVideo,
        playTimer
    ]);

    // TODO: Maybe switch PlayWrapper to React.Fragment if we want each of the potential subcomponents
    // to be on the same hierarchical level
    return (
        <LiveFeedPlayWrapper
            id={`live-feed-play-${playData.atBatIndex}`}
            transitionLatestChild={isCurrentPlay}
        >
            { newAnimDivs }
        </LiveFeedPlayWrapper>
    );
};

LiveFeedPlay.propTypes = {
    playData: PropTypes.object.isRequired,
    teams: PropTypes.object.isRequired,
    offset: PropTypes.bool,
    homeNameFallback: PropTypes.string,
    awayNameFallback: PropTypes.string,
    reverseChron: PropTypes.bool,
    resultVideo: PropTypes.object,
    isLoading: PropTypes.bool,
    deviceInteraction: PropTypes.object,
    language: PropTypes.string,
    currentPlayIndex: PropTypes.number,
    showAllPitches: PropTypes.bool,
    playMetrics: PropTypes.object,
    playerMap: PropTypes.object,
    showBasesText: PropTypes.bool,
    showAnimations: PropTypes.bool,
    openAtBatOverlay: PropTypes.func,
    showPitchesOnly: PropTypes.bool,
    playTimer: PropTypes.number
};

export default LiveFeedPlay;
